import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { PILLARS, SCALE_LABELS } from "@/data/pillars";
import { useDiagnostic } from "@/context/DiagnosticContext";

// Build a flat list of 35 steps
const STEPS = PILLARS.flatMap((p, pi) =>
  p.questions.map((q, qi) => ({
    pillarIndex: pi,
    pillarId: p.id,
    pillarName: p.name,
    pillarDesc: p.description,
    questionIndex: qi,
    question: q,
  }))
);

export default function Diagnostic() {
  const navigate = useNavigate();
  const { company, answers, setAnswer } = useDiagnostic();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!company.nome || !company.empresa || !company.email) {
      navigate("/dados");
    }
  }, [company, navigate]);

  const total = STEPS.length;
  const current = STEPS[step];
  const currentValue =
    answers[current.pillarId]?.[current.questionIndex] ?? null;
  const progress = Math.round(((step + (currentValue ? 1 : 0)) / total) * 100);

  const canGoNext = currentValue !== null;
  const isLast = step === total - 1;

  const next = () => {
    if (!canGoNext) return;
    if (isLast) {
      navigate("/processando");
    } else {
      setStep((s) => Math.min(total - 1, s + 1));
    }
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const answeredPerPillar = useMemo(() => {
    const map = {};
    PILLARS.forEach((p) => {
      map[p.id] = (answers[p.id] || []).filter((v) => v !== null && v !== undefined)
        .length;
    });
    return map;
  }, [answers]);

  return (
    <div data-testid="diagnostic-page" className="min-h-[calc(100vh-112px)]">
      {/* Progress */}
      <div className="sticky top-28 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span data-testid="progress-label" className="uppercase tracking-[0.18em] font-semibold">
              Etapa 2 · Diagnóstico
            </span>
            <span data-testid="progress-percent" className="font-mono text-[#0F172A]">
              {progress}% concluído · {step + 1}/{total}
            </span>
          </div>
          <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              data-testid="progress-bar"
              className="h-full bg-[#0F172A] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-12 gap-10">
        {/* Pillars sidebar */}
        <aside className="lg:col-span-4 order-2 lg:order-1">
          <div className="hub-eyebrow">Pilares</div>
          <div className="mt-4 space-y-2">
            {PILLARS.map((p, i) => {
              const active = i === current.pillarIndex;
              const done = answeredPerPillar[p.id] === 5;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() =>
                    setStep(STEPS.findIndex((s) => s.pillarId === p.id))
                  }
                  data-testid={`pillar-nav-${p.id}`}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                    active
                      ? "bg-[#0F172A] text-white border-[#0F172A]"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div
                        className={`text-[10px] tracking-[0.2em] uppercase ${active ? "text-[#D97706]" : "text-slate-400"}`}
                      >
                        Pilar {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="mt-0.5 font-display font-medium text-sm tracking-tight">
                        {p.name}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {done ? (
                        <CheckCircle2
                          size={16}
                          className={active ? "text-[#16A34A]" : "text-[#16A34A]"}
                        />
                      ) : (
                        <span
                          className={`text-[11px] font-mono ${active ? "text-slate-300" : "text-slate-400"}`}
                        >
                          {answeredPerPillar[p.id]}/5
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Question */}
        <div className="lg:col-span-8 order-1 lg:order-2">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <div className="hub-eyebrow">
                {current.pillarName} · Pergunta {current.questionIndex + 1} de 5
              </div>
              <h2
                data-testid="current-question"
                className="mt-4 font-display font-medium text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15]"
              >
                {current.question}
              </h2>
              <p className="mt-4 text-slate-600 max-w-xl">
                Considere a realidade atual da <strong>{company.empresa}</strong>.
                Responda de <strong>1</strong> (discordo totalmente) a{" "}
                <strong>5</strong> (concordo totalmente).
              </p>

              <div className="mt-10 grid grid-cols-5 gap-2 sm:gap-3">
                {[1, 2, 3, 4, 5].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`scale-${v}`}
                    onClick={() =>
                      setAnswer(current.pillarId, current.questionIndex, v)
                    }
                    className={`hub-scale-btn ${currentValue === v ? "selected" : ""}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex justify-between text-xs text-slate-500">
                <span>{SCALE_LABELS[1]}</span>
                <span>{SCALE_LABELS[5]}</span>
              </div>

              {currentValue !== null && (
                <div className="mt-4 text-sm text-[#0F172A]">
                  Selecionado:{" "}
                  <strong className="text-[#D97706]">
                    {SCALE_LABELS[currentValue]}
                  </strong>
                </div>
              )}

              <div className="mt-12 pt-8 border-t border-slate-200 flex items-center justify-between gap-4">
                <button
                  type="button"
                  data-testid="btn-prev-question"
                  onClick={step === 0 ? () => navigate("/dados") : prev}
                  className="hub-btn-secondary"
                >
                  <ArrowLeft size={16} />{" "}
                  {step === 0 ? "Voltar aos dados" : "Anterior"}
                </button>
                <button
                  type="button"
                  data-testid="btn-next-question"
                  onClick={next}
                  disabled={!canGoNext}
                  className="hub-btn-primary"
                >
                  {isLast ? "Concluir diagnóstico" : "Próxima"}
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
